/**
 * CurvedText — renders SVG text following a circular arc.
 * Used to place dedication text, station name, etc. along grooves.
 */
import { useId } from "react";

interface CurvedTextProps {
  text: string;
  radius: number;
  startAngle?: number;
  arcSpan?: number;
  fontSize?: number;
  letterSpacing?: number;
  className?: string;
  flip?: boolean;
  opacity?: number;
}

export default function CurvedText({
  text,
  radius,
  startAngle = -60,
  fontSize = 9,
  letterSpacing = 2,
  className = "",
  flip = false,
  opacity = 1,
}: CurvedTextProps) {
  const uid = useId();
  const size = (radius + fontSize + 4) * 2;
  const cx = size / 2;
  const cy = size / 2;

  const pathId = `arc-${uid.replace(/:/g, "")}`;

  const sweep = flip ? 1 : 1;
  const sa = (startAngle - 90) * (Math.PI / 180);
  const charWidthEstimate = fontSize * 0.62 + letterSpacing;
  const arcSpanDeg = Math.min((text.length * charWidthEstimate / radius) * (180 / Math.PI), 260);
  const ea = sa + (arcSpanDeg * Math.PI) / 180;

  const x1 = cx + radius * Math.cos(sa);
  const y1 = cy + radius * Math.sin(sa);
  const x2 = cx + radius * Math.cos(ea);
  const y2 = cy + radius * Math.sin(ea);

  const largeArc = arcSpanDeg > 180 ? 1 : 0;

  const d = `M ${x1} ${y1} A ${radius} ${radius} 0 ${largeArc} ${sweep} ${x2} ${y2}`;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none ${className}`}
      style={{ opacity }}
    >
      <defs>
        <path id={pathId} d={d} />
      </defs>
      <text
        fill="currentColor"
        fontSize={fontSize}
        fontFamily="var(--font-sans)"
        letterSpacing={letterSpacing}
        textAnchor="start"
      >
        <textPath href={`#${pathId}`}>
          {text.toUpperCase()}
        </textPath>
      </text>
    </svg>
  );
}
